// The Jobs destination: every job, in the two halves of the workflow plus Done, grouped by
// step, with filters so the list never has to be read top to bottom.

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCrumb } from '../frame/Frame';
import { useRows, type Row } from '../lib/api';
import { daysSince, hours } from '../lib/format';
import { jobTitle, useAllJobs, type JobSummary } from '../lib/jobs';
import { useProgress } from '../lib/progress';
import { useSession } from '../lib/session';
import { Empty, Head, Loading, Money, onLink, Panel, Seg, Tag } from '../ui/kit';
import { splitWorkflow } from '@zerogap/engine';

type Half = 'pipeline' | 'production' | 'done';

export function Jobs() {
  useCrumb([{ label: 'Jobs' }]);
  const s = useSession();
  const all = useAllJobs();
  const types = useRows('job_type', { order: ['name'] });
  const dates = useRows('job_step');
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const half = (params.get('view') as Half) || 'pipeline';
  const [typeId, setTypeId] = useState('');
  const [flag, setFlag] = useState('');
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState('');
  // the job's own checklist: the four opportunity-level steps retired into the pipeline (2026-10-19) are left out
  const halves = useMemo(() => splitWorkflow(all.steps.filter((s) => !s.retired_at) as any[]) as { pipeline: Row[]; production: Row[] }, [all.steps]);
  const progress = useProgress(all.steps);

  if (all.loading) return <Loading what="Loading jobs" />;

  // "done" is the step whose key is `closed`, not whichever step happens to be last
  const isDone = (j: JobSummary) => j.done;
  // a job left on a retired step (made before 2026-10-19) reads as not yet started: the first Estimating step
  const live = new Set([...halves.pipeline, ...halves.production].map((x) => x.id));
  const stepOf = (j: JobSummary): string | null => (j.job.stage_id && live.has(j.job.stage_id) ? j.job.stage_id : null);
  const inHalf = (j: JobSummary, h: Half) => {
    if (h === 'done') return isDone(j);
    if (isDone(j)) return false;
    const ids = new Set(halves[h].map((x) => x.id));
    const at = stepOf(j);
    return at ? ids.has(at) : h === 'pipeline';
  };
  const needsMe = (j: JobSummary) => !!j.stage && j.stage.owner_role === s.role;
  const daysAt = (j: JobSummary) => {
    const d = dates.rows.find((x) => x.job_id === j.job.id && x.workflow_step_id === stepOf(j));
    return d?.actual_date ? daysSince(d.actual_date) : null;
  };
  const text = (j: JobSummary) => `${jobTitle(j)} ${j.stage?.name ?? ''}`.toLowerCase();

  const shown = all.jobs.filter(
    (j) =>
      inHalf(j, half) &&
      (!typeId || j.job.job_type_id === typeId) &&
      (!mine || needsMe(j)) &&
      (!q || text(j).includes(q.toLowerCase())) &&
      (!flag ||
        (flag === 'unverified' && (j.res?.totals.unverified_items ?? 0) > 0) ||
        (flag === 'fix' && (j.res?.totals.warnings ?? 0) > 0) ||
        (flag === 'pass1' && j.itemCount > 0 && !j.job.pass1_completed_at) ||
        (flag === 'pass2' && !!j.job.pass1_completed_at && !j.job.pass2_completed_at)),
  );
  const counts = { pipeline: all.jobs.filter((j) => inHalf(j, 'pipeline')).length, production: all.jobs.filter((j) => inHalf(j, 'production')).length, done: all.jobs.filter((j) => inHalf(j, 'done')).length };
  const stepsHere: Row[] = half === 'done' ? [{ id: 'done', name: 'Done', sort: 0 }] : halves[half];
  const groupOf = (j: JobSummary) => (half === 'done' ? 'done' : stepOf(j) ?? stepsHere[0]?.id);

  const row = (j: JobSummary) => {
    const d = daysAt(j);
    return (
      // the title is a real link (Cmd-click to a new tab, right-click copy); the rest of the row still opens the job (design review D8)
      <tr key={j.job.id} className="click" onClick={(e) => !onLink(e) && nav(`/jobs/${j.job.id}`)}>
        <td>
          <Link className="rowlink" to={`/jobs/${j.job.id}`}>
            <b>{jobTitle(j)}</b>
          </Link>
          {j.job.locked_at && <> 🔒</>}
          {needsMe(j) && (
            <>
              {' '}
              <Tag>yours</Tag>
            </>
          )}
        </td>
        <td>
          {j.stage?.name ?? '—'}
          {d !== null && <small> · {d} d</small>}
          {j.opp?.outcome === 'lost' && <small> · lost</small>}
        </td>
        <td style={{ minWidth: 90 }}>
          <small>{progress.of(j.job).overall_pct}%</small>
          <div className="meter">
            <i style={{ width: `${progress.of(j.job).overall_pct}%` }} />
          </div>
        </td>
        <td>{types.rows.find((t) => t.id === j.job.job_type_id)?.name ?? '—'}</td>
        <td className="right num">{j.itemCount}</td>
        <td className="right num">{hours(j.res?.totals.labor_minutes)}</td>
        <td className="right">
          <Money cents={j.res?.totals.price_cents} />
        </td>
        <td>
          {j.res && j.res.totals.unverified_items > 0 && <Tag kind="un">{j.res.totals.unverified_items} unverified</Tag>}{' '}
          {j.res && j.res.totals.warnings > 0 && <Tag kind="un">{j.res.totals.warnings} to fix</Tag>}{' '}
          {j.itemCount > 0 && !j.job.pass1_completed_at && <Tag kind="off">pass 1</Tag>}
        </td>
      </tr>
    );
  };

  return (
    <div className="stack">
      <Head title="Jobs" sub="One job per scope. A job is an estimate until its opportunity is won; the lead itself moves on the Pipeline.">
        <Seg
          items={[
            { key: 'pipeline', label: `Estimating (${counts.pipeline})` },
            { key: 'production', label: `Production (${counts.production})` },
            { key: 'done', label: `Done (${counts.done})` },
          ]}
          value={half}
          onChange={(k) => setParams({ view: k }, { replace: true })}
        />
      </Head>
      <div className="row mid">
        <input type="search" placeholder="Find a job" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="read" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          <option value="">Every job type</option>
          {types.rows.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select className="read" value={flag} onChange={(e) => setFlag(e.target.value)}>
          <option value="">Any state</option>
          <option value="unverified">Has unverified items</option>
          <option value="fix">Has something to fix</option>
          <option value="pass1">Waiting on pass 1</option>
          <option value="pass2">Waiting on pass 2</option>
        </select>
        <button type="button" className={`tag read${mine ? ' on' : ''}`} onClick={() => setMine(!mine)} title="Only jobs whose current step is yours">
          Needs me
        </button>
      </div>
      {shown.length === 0 ? (
        <Empty>{all.jobs.length === 0 ? 'No jobs yet. Use + New in the top bar.' : 'Nothing matches.'}</Empty>
      ) : (
        stepsHere
          .filter((step) => shown.some((j) => groupOf(j) === step.id))
          .map((step) => (
            <Panel key={step.id} label={`${step.name} (${shown.filter((j) => groupOf(j) === step.id).length})${step.owner_role ? ` · ${step.owner_role}'s step` : ''}`}>
              <div className="tblwrap">
                <table>
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Step</th>
                      <th>Progress</th>
                      <th>Type</th>
                      <th className="right">Items</th>
                      <th className="right">Hours</th>
                      <th className="right">Price</th>
                      <th>Flags</th>
                    </tr>
                  </thead>
                  <tbody>{shown.filter((j) => groupOf(j) === step.id).map(row)}</tbody>
                </table>
              </div>
            </Panel>
          ))
      )}
    </div>
  );
}
